// @ts-strict-ignore
import {
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { OverlayService } from './overlay.service';
import { generateRandomString } from '@fc-shared/utils/data.utils';

@Directive({
  host: {
    '[attr.data-overlay-trigger]': 'overlayId',
    '[attr.aria-controls]': 'overlayId',
    '[attr.aria-expanded]': 'isOpen()',
    '[style.anchor-name]': 'anchorName',
  },
})
export abstract class OverlayTriggerBase implements OnDestroy {
  protected elementRef = inject(ElementRef<HTMLElement>);
  protected overlayService = inject(OverlayService);

  readonly overlayId = `fc-overlay-${generateRandomString(6)}`;
  readonly anchorName = `--${this.overlayId}`;
  protected isOpen = signal(false);
  isOpenSignal = this.isOpen.asReadonly();
  protected overlayElement: HTMLElement | null = null;
  protected openMode: 'click' | 'hover' = 'click';
  private listenersRegistered = false;

  ngOnDestroy(): void {
    if (this.overlayElement) {
      this.overlayElement.removeEventListener('toggle', this.handleToggle);
      this.cleanupOverlayListeners();
    }
  }

  private ensureOverlayElement(): boolean {
    if (!this.overlayElement) {
      this.overlayElement = this.overlayService.get(this.overlayId);
    }

    if (!this.overlayElement) {
      console.warn(`[fcOverlay] Overlay element "${this.overlayId}" not found`);
      return false;
    }

    if (!this.listenersRegistered) {
      this.overlayElement.addEventListener('toggle', this.handleToggle);
      this.setupOverlayListeners();
      this.listenersRegistered = true;
    }
    return true;
  }

  open(): void {
    this.showOverlay();
  }

  close(): void {
    this.hideOverlay();
  }

  toggle(): void {
    this.toggleOverlay();
  }

  protected showOverlay(): void {
    if (!this.ensureOverlayElement() || this.isOpen()) return;

    this.overlayService.registerOpen(this.overlayElement, this.openMode);
    this.overlayElement.showPopover();
  }

  protected hideOverlay(): void {
    if (!this.overlayElement || !this.isOpen()) return;

    this.overlayService.registerClose(this.overlayElement);
    this.overlayElement.hidePopover();
  }

  protected toggleOverlay(): void {
    if (this.isOpen()) {
      this.hideOverlay();
    } else {
      this.showOverlay();
    }
  }

  private handleToggle = (event: Event): void => {
    const toggleEvent = event as ToggleEvent;
    this.isOpen.set(toggleEvent.newState === 'open');
  };

  protected setupOverlayListeners(): void {}

  protected cleanupOverlayListeners(): void {}
}
